import {memo, useCallback} from 'react'
import {ConnectionQuality, Track} from 'livekit-client'
import {
  isTrackReference,
  useConnectionQualityIndicator,
  useIsMuted,
  useParticipantTile,
  VideoTrack,
} from '@livekit/components-react'
import type {TrackReferenceOrPlaceholder} from '@livekit/components-react'
import {Mic, MicOff, MonitorUp, SignalLow, SignalMedium, SignalZero} from 'lucide-react'
import {cn} from '@/shared/lib/cn'

interface ParticipantTileProps {
  trackRef: TrackReferenceOrPlaceholder
  className?: string
  isFocused?: boolean
  isFullscreen?: boolean
}

function getInitial(name: string | undefined, identity: string): string {
  const source = name || identity
  return source.charAt(0).toUpperCase()
}

function QualityIcon({quality}: {quality: ConnectionQuality}) {
  const shared = 'size-3.5'

  switch (quality) {
    case ConnectionQuality.Good:
      return <SignalMedium className={cn(shared, 'text-signal-warning')} />
    case ConnectionQuality.Poor:
      return <SignalLow className={cn(shared, 'text-signal-error')} />
    case ConnectionQuality.Lost:
      return <SignalZero className={cn(shared, 'text-signal-error')} />
    default:
      return null
  }
}

export const ParticipantTile = memo(function ParticipantTile({
  trackRef,
  className,
  isFocused,
  isFullscreen,
}: ParticipantTileProps) {
  const {elementProps} = useParticipantTile<HTMLDivElement>({
    trackRef,
    disableSpeakingIndicator: true,
    htmlProps: {},
  })

  const {quality} = useConnectionQualityIndicator({participant: trackRef.participant})

  const micTrackRef: TrackReferenceOrPlaceholder = {
    participant: trackRef.participant,
    source: Track.Source.Microphone,
  }
  const isMicMuted = useIsMuted(micTrackRef)

  const isScreenShare = trackRef.source === Track.Source.ScreenShare
  const hasVideoPublication =
    isTrackReference(trackRef) &&
    (trackRef.publication?.kind === 'video' ||
      trackRef.source === Track.Source.Camera ||
      isScreenShare)

  const isCameraMuted = useIsMuted(trackRef)
  const showVideo = hasVideoPublication && !isCameraMuted

  const participantName = trackRef.participant.name || trackRef.participant.identity
  const initial = getInitial(trackRef.participant.name, trackRef.participant.identity)

  const handleSubscribe = useCallback(() => {}, [])

  // const needsMirror = !isLocalCamera

  return (
    <div
      {...elementProps}
      className={cn(
        'relative flex h-full w-full items-center justify-center bg-muted/80',
        isFullscreen ? 'rounded-none' : 'rounded-lg',
        className,
      )}
    >
      {showVideo ? (
        <>
          {/* Blurred video background — same track, one decoder, GPU blur */}
          <div
            className={cn(
              'absolute inset-0 overflow-hidden',
              isFullscreen ? 'rounded-none' : 'rounded-lg',
            )}
            aria-hidden
          >
            <VideoTrack
              trackRef={trackRef}
              onSubscriptionStatusChanged={handleSubscribe}
              className={cn('absolute inset-0 size-full scale-150 object-cover blur-2xl')}
            />
          </div>
          <VideoTrack
            trackRef={trackRef}
            onSubscriptionStatusChanged={handleSubscribe}
            className={cn(
              'relative z-0 h-full w-full bg-transparent!',
              isFocused && 'object-contain!',
            )}
          />
        </>
      ) : null}

      {!showVideo && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex size-14 items-center justify-center rounded-full bg-muted text-xl font-semibold text-foreground/80">
            {initial}
          </div>
        </div>
      )}

      {/* Bottom metadata bar */}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 px-2.5 py-1.5">
        {isScreenShare && <MonitorUp className="size-3.5 text-signal-info" />}

        {!isScreenShare && (
          <span
            className={cn(
              'flex items-center justify-center rounded-full p-0.5',
              isMicMuted && 'bg-signal-error/70',
            )}
          >
            {isMicMuted ? (
              <MicOff className="size-3 text-foreground" />
            ) : (
              <Mic className={cn('size-3', 'text-foreground/70')} />
            )}
          </span>
        )}

        <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/90">
          {isScreenShare ? `Экран: ${participantName}` : participantName}
        </span>

        <QualityIcon quality={quality} />
      </div>
    </div>
  )
})
